<template>
  <div class="row justify-content-center">
    <div class="col-sm-9 col-md-8 col-lg-6 col-xl-4">
      <form>
        <div class="form-group">
          <label for="foodName">{{ $t("forms.name.label") }}</label>
          <input
            :class="['form-control', valid.name ? 'is-valid' : 'is-invalid']"
            id="foodName"
            aria-describedby="foodNameHelp"
            :placeholder="$t('forms.name.placeholder')"
            v-model="foodItem.name"
            @input="validateLength('name')"
          />
          <small id="foodNameHelp" class="form-text text-muted"></small>
        </div>
        <div class="form-group">
          <label for="foodAmount">{{ $t("forms.amount.label") }}</label>
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text" @click="decreaseAmout">
                <v-icon name="minus" />
              </span>
            </div>
            <input
              :class="[
                'form-control',
                valid.amount ? 'is-valid' : 'is-invalid'
              ]"
              id="foodAmount"
              aria-describedby="foodAmountHelp"
              :placeholder="$t('forms.amount.placeholder')"
              v-model="foodItem.amount"
              @input="validateAmount"
            />
            <div class="input-group-append">
              <span class="input-group-text" @click="increaseAmount">
                <v-icon name="plus" />
              </span>
            </div>
          </div>
          <small id="foodAmountHelp" class="form-text text-muted"></small>
        </div>
        <div class="form-group">
          <label for="foodCategories">{{ $t("forms.categories.label") }}</label>
          <!-- 2 panels version -->
          <div class="row categories-panels">
            <div class="col-6 d-flex flex-column">
              <strong>Selected categories</strong>
              <div class="flex-fill">
                <span
                  class="badge category-badge"
                  v-for="categoryId in foodItem.categories"
                  :key="categoryById(categoryId).created"
                  :id="categoryId"
                  :style="{
                    backgroundColor: '#' + categoryById(categoryId).color + '80'
                  }"
                  @click="unSelectCategory(categoryId)"
                  >{{ categoryById(categoryId).name }}</span
                >
              </div>
            </div>
            <div class="col-6 d-flex flex-column">
              <strong>Available categories</strong>
              <div class="flex-fill">
                <span
                  class="badge category-badge"
                  v-for="categoryId in unselectedCatIds"
                  :key="categoryById(categoryId).created"
                  :id="categoryId"
                  :style="{
                    backgroundColor: '#' + categoryById(categoryId).color + '80'
                  }"
                  @click="selectCategory(categoryId)"
                  >{{ categoryById(categoryId).name }}</span
                >
              </div>
            </div>
          </div>
          <!-- 2 panels version end -->
          <small id="foodCategoriesHelp" class="form-text text-muted">{{
            $t("forms.categories.helper")
          }}</small>
        </div>
        <div class="form-group">
          <label for="foodNotes">{{ $t("forms.notes.label") }}</label>
          <input
            class="form-control is-valid"
            id="foodNotes"
            aria-describedby="foodNotesHelp"
            :placeholder="$t('forms.notes.placeholder')"
            v-model="foodItem.notes"
          />
          <small id="foodNotesHelp" class="form-text text-muted">
            {{ $t("forms.notes.helper") }}
          </small>
        </div>
        <div class="d-flex justify-content-center">
          <button
            type="submit"
            class="btn btn-success"
            :disabled="!validForm"
            :aria-disabled="!validForm"
            @click.prevent="submit"
          >
            {{ $t(id ? "buttons.save" : "buttons.create") }}
          </button>
          <div class="btn btn-danger ml-3" @click="$router.go(-1)">
            {{ $t("buttons.cancel") }}
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import "vue-awesome/icons/plus";
import "vue-awesome/icons/minus";
import { randomKey, moveBetweenArrays } from "./../../tools";

export default {
  name: "FoodForm",
  props: {
    id: { type: String, default: null },
    foodItem: {
      type: Object,
      default() {
        return {
          name: "",
          amount: 1,
          categories: [],
          notes: ""
        };
      }
    },
    preselectCategories: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      valid: {
        name: false,
        amount: false
      },
      unselectedCatIds: []
    };
  },
  computed: {
    ...mapGetters(["pantryAuthData", "categories", "categoryById"]),
    validForm() {
      for (let property in this.valid) {
        if (!this.valid[property]) {
          return false;
        }
      }
      return true;
    }
  },
  watch: {
    // in case loaded later
    categories(newvalue, oldvalue) {
      this.buildUnselectedCategories();
    },
    foodItem(newvalue, oldvalue) {
      this.validateLength("name");
      this.validateAmount();
      this.buildUnselectedCategories();
    }
  },
  methods: {
    validateLength(name) {
      this.valid[name] =
        this.foodItem.hasOwnProperty(name) && !!this.foodItem[name].length;
    },
    validateAmount() {
      let valid = false;
      if (this.foodItem.hasOwnProperty("amount")) {
        const intVal = parseInt(this.foodItem.amount);
        if (!isNaN(intVal)) {
          this.foodItem.amount = intVal;
          valid = this.foodItem.amount >= 0;
        }
      }
      this.valid.amount = valid;
    },
    increaseAmount() {
      this.foodItem.amount = isNaN(this.foodItem.amount)
        ? 1
        : this.foodItem.amount + 1;
      this.valid.amount = true;
    },
    decreaseAmout() {
      this.foodItem.amount =
        isNaN(this.foodItem.amount) || this.foodItem.amount < 1
          ? 0
          : this.foodItem.amount - 1;
      this.valid.amount = true;
    },
    buildUnselectedCategories() {
      this.unselectedCatIds = [];
      for (const id in this.categories) {
        let selected = false;
        for (const key in this.foodItem.categories) {
          if (this.foodItem.categories[key] === id) {
            selected = true;
            break;
          }
        }
        if (!selected) {
          this.unselectedCatIds.push(id);
        }
      }
    },
    selectCategory(id) {
      moveBetweenArrays(id, this.unselectedCatIds, this.foodItem.categories);
    },
    unSelectCategory(id) {
      moveBetweenArrays(id, this.foodItem.categories, this.unselectedCatIds);
    },
    submit() {
      if (this.validForm) {
        const now = new Date().getTime();
        this.foodItem.created = this.foodItem.created
          ? this.foodItem.created
          : now;
        this.foodItem.updated = now;
        const curr_id = this.id ? this.id : randomKey();
        // TODO: show loading?
        this.$store.state.resources.food
          .update(
            {
              ...this.pantryAuthData,
              id: curr_id
            },
            this.foodItem
          )
          .then(
            /*response*/ () => {
              this.$emit("foodSubmitted", {
                id: curr_id,
                foodItem: this.foodItem
              });
              // console.log(response.body.name);
            },
            error => {
              console.log(error);
            }
          );
      }
    }
  },
  created() {
    if (!this.foodItem.categories.length && this.preselectCategories.length) {
      this.foodItem.categories = this.preselectCategories;
    }
    this.validateLength("name");
    this.validateAmount();
    this.buildUnselectedCategories();
  }
};
</script>

<style scoped lang="scss">
@import "./../../assets/scss/variables";
.input-group-text {
  border-color: #808080;
  cursor: pointer;
  user-select: none;
  &:hover {
    background-color: #cccccf;
    box-shadow: 0 0 10px -5px darken(#0f3255, 40%);
  }
  &:active {
    background-color: darken(#cccccf, 5%);
    box-shadow: 0 0 5px darken(#0f3255, 30%);
    transform: scale(0.94);
  }
}
.categories-panels {
  .col-6 {
    &:first-child {
      padding-right: 5px;
    }
    &:last-child {
      padding-left: 5px;
    }
  }
  .flex-fill {
    border-radius: 0.25rem;
    padding: 0.5rem;
    border: 1px solid #808080;
  }
}
.category-badge {
  cursor: pointer;
  user-select: none;
}
</style>
